import { z } from "zod";
const zoomString = z.string().regex(/^(g|\d+)$/, { message: "Must be a zoom level (0-24) or 'g' to guess" });
const zoomInt = z.number().int().min(0).max(24);
const detailInt = z.number().int().min(0).max(16);
const nonEmptyString = z.string().min(1);
const sourceSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("DatasetVersion"),
        datasetVersionId: z.number(),
    }),
    z.object({
        type: z.literal("TilesetSource"),
        sourceId: z.number(),
    }),
]);
/**
 * Wire-format zod schema for tippecanoe per-layer options.
 *
 * Subset of the full `TippecanoeOptions` interface defined in `@project/api`.
 * Backend-only fields (`output`, `prefilter`, `postfilter`, `read-parallel`)
 * are intentionally omitted — they are set by the server, not accepted from clients.
 * Type parity (schema ⊆ type) is asserted in `@project/api`.
 */
export const TippecanoeOptionsZodSchema = z.object({
    // Output tileset (`output` is set by the backend; not accepted from clients)
    "allow-existing": z.boolean().optional(),
    // Tileset description and attribution
    "name": z.string().optional(),
    "attribution": z.string().optional(),
    "description": z.string().optional(),
    // Input files and layer names (required)
    "layer": nonEmptyString,
    // `read-parallel` intentionally omitted — backend auto-enables it based on input file type.
    // Projection of input
    "projection": z.enum(["EPSG:4326", "EPSG:3857"]).optional(),
    // Zoom levels
    "maximum-zoom": zoomString.optional(),
    "smallest-maximum-zoom-guess": zoomInt.optional(),
    "minimum-zoom": zoomInt.optional(),
    "extend-zooms-if-still-dropping": z.boolean().optional(),
    "extend-zooms-if-still-dropping-maximum": z.number().int().min(0).optional(),
    "generate-variable-depth-tile-pyramid": z.boolean().optional(),
    "one-tile": z.string().optional(),
    // Tile resolution
    "full-detail": detailInt.optional(),
    "low-detail": detailInt.optional(),
    "minimum-detail": detailInt.optional(),
    "extra-detail": detailInt.optional(),
    // Filtering feature attributes
    "exclude": z.array(z.string()).optional(),
    "include": z.array(z.string()).optional(),
    "exclude-all": z.boolean().optional(),
    // Modifying feature attributes
    "attribute-type": z.array(z.string()).optional(),
    "attribute-description": z.array(z.string()).optional(),
    "accumulate-attribute": z.array(z.string()).optional(),
    "set-attribute": z.array(z.string()).optional(),
    "empty-csv-columns-are-null": z.boolean().optional(),
    "convert-stringified-ids-to-numbers": z.boolean().optional(),
    "use-attribute-for-id": z.string().optional(),
    "single-precision": z.boolean().optional(),
    "maximum-string-attribute-length": z.number().int().min(0).optional(),
    // Filtering features by attributes
    // `feature-filter-file` intentionally omitted — filesystem path set by the server.
    "feature-filter": z.string().optional(),
    // Dropping a fixed fraction of features by zoom level
    "drop-rate": z.string().optional(),
    "base-zoom": z.string().optional(),
    "retain-points-multiplier": z.number().min(0).optional(),
    "drop-denser": z.number().min(0).max(100).optional(),
    "limit-base-zoom-to-maximum-zoom": z.boolean().optional(),
    "drop-lines": z.boolean().optional(),
    "drop-polygons": z.boolean().optional(),
    "cluster-distance": z.number().int().min(0).optional(),
    "cluster-maxzoom": z.string().optional(),
    "preserve-point-density-threshold": z.number().min(0).optional(),
    // Dropping a fraction of features to keep under tile size limits
    "drop-densest-as-needed": z.boolean().optional(),
    "drop-fraction-as-needed": z.boolean().optional(),
    "drop-smallest-as-needed": z.boolean().optional(),
    "coalesce-smallest-as-needed": z.boolean().optional(),
    "coalesce-densest-as-needed": z.boolean().optional(),
    "coalesce-fraction-as-needed": z.boolean().optional(),
    "force-feature-limit": z.boolean().optional(),
    "cluster-densest-as-needed": z.boolean().optional(),
    // Dropping tightly overlapping features
    "gamma": z.number().min(0).optional(),
    "increase-gamma-as-needed": z.boolean().optional(),
    // Line and polygon simplification
    "simplification": z.number().min(0).optional(),
    "no-line-simplification": z.boolean().optional(),
    "simplify-only-low-zooms": z.boolean().optional(),
    "simplification-at-maximum-zoom": z.number().min(0).optional(),
    "no-simplification-of-shared-nodes": z.boolean().optional(),
    "no-tiny-polygon-reduction": z.boolean().optional(),
    "no-tiny-polygon-reduction-at-maximum-zoom": z.boolean().optional(),
    "tiny-polygon-size": z.number().min(0).optional(),
    "visvalingam": z.boolean().optional(),
    // Attempts to improve shared polygon boundaries
    "detect-shared-borders": z.boolean().optional(),
    "grid-low-zooms": z.boolean().optional(),
    // Controlling clipping to tile boundaries
    "buffer": z.number().int().min(0).optional(),
    "no-clipping": z.boolean().optional(),
    "no-duplication": z.boolean().optional(),
    // Reordering features within each tile
    "preserve-input-order": z.boolean().optional(),
    "coalesce": z.boolean().optional(),
    "reorder": z.boolean().optional(),
    "reverse": z.boolean().optional(),
    "hilbert": z.boolean().optional(),
    "order-by": z.array(z.string()).optional(),
    "order-descending-by": z.array(z.string()).optional(),
    "order-smallest-first": z.boolean().optional(),
    "order-largest-first": z.boolean().optional(),
    // Adding calculated attributes
    "calculate-feature-density": z.boolean().optional(),
    "generate-ids": z.boolean().optional(),
    "calculate-feature-index": z.boolean().optional(),
    // Trying to correct bad source geometry
    "detect-longitude-wraparound": z.boolean().optional(),
    "use-source-polygon-winding": z.boolean().optional(),
    "reverse-source-polygon-winding": z.boolean().optional(),
    "clip-bounding-box": z.string().optional(),
    "convert-polygons-to-label-points": z.boolean().optional(),
    // Setting or disabling tile size limits
    "maximum-tile-bytes": z.number().int().min(0).optional(),
    "maximum-tile-features": z.number().int().min(0).optional(),
    "limit-tile-feature-count": z.number().int().min(0).optional(),
    "limit-tile-feature-count-at-maximum-zoom": z.number().int().min(0).optional(),
    "no-feature-limit": z.boolean().optional(),
    "no-tile-size-limit": z.boolean().optional(),
    "no-tile-compression": z.boolean().optional(),
    "no-tile-stats": z.boolean().optional(),
    "tile-stats-attributes-limit": z.number().int().min(0).optional(),
    "tile-stats-sample-values-limit": z.number().int().min(0).optional(),
    "tile-stats-values-limit": z.number().int().min(0).optional(),
    // `prefilter` / `postfilter` intentionally omitted — they accept arbitrary shell commands
    // and must never be accepted over the wire (RCE surface).
});
/**
 * Wire-format zod schema for tile-join options.
 *
 * `output` / `read-from` are backend-set paths; not accepted from clients.
 */
export const TileJoinOptionsZodSchema = z.object({
    // Overzooming
    "overzoom": z.boolean().optional(),
    "buffer": z.number().int().min(0).optional(),
    // Tileset description and attribution
    "attribution": z.string().optional(),
    "name": nonEmptyString,
    "description": z.string().optional(),
    // Layer filtering and naming
    "layer": z.array(z.string()).optional(),
    "exclude-layer": z.array(z.string()).optional(),
    "rename-layer": z.array(z.string()).optional(),
    // Zoom levels
    "maximum-zoom": zoomInt.optional(),
    "minimum-zoom": zoomInt.optional(),
    // `csv` and `feature-filter-file` intentionally omitted — filesystem paths are
    // set by the server, not accepted from clients.
    // Filtering features and feature attributes
    "exclude": z.array(z.string()).optional(),
    "exclude-all": z.boolean().optional(),
    "include": z.array(z.string()).optional(),
    "if-matched": z.boolean().optional(),
    "feature-filter": z.string().optional(),
    "empty-csv-columns-are-null": z.boolean().optional(),
    // Setting or disabling tile size limits
    "no-tile-size-limit": z.boolean().optional(),
    "no-tile-compression": z.boolean().optional(),
    "no-tile-stats": z.boolean().optional(),
    "tile-stats-attributes-limit": z.number().int().min(0).optional(),
    "tile-stats-sample-values-limit": z.number().int().min(0).optional(),
    "tile-stats-values-limit": z.number().int().min(0).optional(),
});
/**
 * Zod schema for the full tileset config posted by `createInitialVersion`.
 */
export const TilesetConfigSchema = z.object({
    description: z.string().optional(),
    attribution: z.string().optional(),
    join_options: TileJoinOptionsZodSchema.optional(),
    layers: z.array(z.object({
        sources: z.array(sourceSchema).min(1),
        options: TippecanoeOptionsZodSchema,
    })).min(1),
});
//# sourceMappingURL=tippecanoeSchema.js.map